import classNames from "classnames";
import { FunctionComponent, useState } from "react";
import { useIntl } from "react-intl";
import BottomSheet from "@Components/molecules/BottomSheet";
import CheckoutAutocompleteInput from "@Components/molecules/cart/CheckoutAutocompleteInput";
import CheckoutPickupLocations from "@Components/molecules/cart/CheckoutPickupLocations";
import CheckoutPickupLocationsSkeleton from "@Components/molecules/cart/CheckoutPickupLocationsSkeleton";
import { ReactComponent as IconSearch } from "@Icons/16x16/search.svg";
import { AutocompletePlace, MondialRelayLocation } from "@Types/addresses";
import { FormError } from "@Types/error";
import { getInputErrorMessages } from "@Utils/error";
import "./style.scss";

interface Props {
  disabled: boolean;
  display: boolean;
  onDismiss: () => void;
  onSelectAddressForPickup: (place: AutocompletePlace | undefined) => void;
  onSelectPickupLocation: (location?: MondialRelayLocation) => void;
  pickupLocations: MondialRelayLocation[];
  shippingErrors: FormError[];
  waitingPickupList: boolean;
}

const CheckoutShippingBottomSheet: FunctionComponent<Props> = ({
  disabled,
  display,
  onDismiss,
  onSelectAddressForPickup,
  onSelectPickupLocation,
  pickupLocations,
  shippingErrors,
  waitingPickupList,
}) => {
  const intl = useIntl();
  const [pickupAddressInput, setPickupAddressInput] = useState<string | undefined>("");

  const handleDismiss = () => {
    setPickupAddressInput("");
    onSelectPickupLocation();
    onSelectAddressForPickup(undefined);
  };

  const handleSelectLocation = (location: MondialRelayLocation) => {
    onDismiss();
    onSelectPickupLocation(location);
  };

  const handleSelectPickupAddress = (place: AutocompletePlace) => {
    setPickupAddressInput(place.formattedAddress);
    onSelectAddressForPickup(place);
  };

  return (
    <BottomSheet display={display} onDismiss={onDismiss} showCloseButton={true}>
      <div className="CheckoutShippingBottomSheet">
        <div className="CheckoutShippingBottomSheet-title">
          {intl.formatMessage({
            id: "cheerz_CheckoutShippingBottomSheet_title__pickup",
          })}
        </div>

        <div
          className={classNames("CheckoutShippingBottomSheet-content", {
            "CheckoutShippingBottomSheet-content--loading": waitingPickupList,
          })}
        >
          <CheckoutAutocompleteInput
            disabled={disabled}
            errorMessage={getInputErrorMessages(shippingErrors, "pickUpAddress")}
            icon={<IconSearch />}
            onChange={(value) => setPickupAddressInput(value)}
            onDismiss={handleDismiss}
            onlyDisplayStreet={false}
            onSelect={handleSelectPickupAddress}
            value={pickupAddressInput}
          />
          <div className="CheckoutShippingBottomSheet-content-description">
            {intl.formatMessage({
              id: "cheerz_CheckoutShippingBottomSheet_description",
            })}
          </div>
          {waitingPickupList ? (
            <CheckoutPickupLocationsSkeleton />
          ) : (
            pickupLocations.length > 0 && (
              <CheckoutPickupLocations
                locations={pickupLocations}
                onSelectLocation={handleSelectLocation}
              />
            )
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default CheckoutShippingBottomSheet;
